import React from 'react'
import { Modal, Button, Alert, Icon, Progress, Input, Select, Spin } from 'antd'
import { LANGUAGE_OPTIONS } from '../../constants/videoLanguages'

const { TextArea } = Input
const { Option } = Select

const isOverwriteConflict = uploadError =>
  typeof uploadError === 'string' &&
  uploadError.toLowerCase().includes('overwrite')

const VoiceUploadDetailsModal = ({
  visible,
  pendingUploadFile,
  uploadError,
  uploading,
  uploadProgress,
  uploadTaskProgress = 0,
  uploadTaskStatus = null,
  uploadTaskMessage = '',
  pendingVoiceName,
  pendingVoiceDescription,
  pendingVoiceLanguage,
  setPendingVoiceName,
  setPendingVoiceDescription,
  setPendingVoiceLanguage,
  onCancel,
  onConfirmUpload,
  onRetryUpload,
  onOverwriteUpload
}) => {
  const showOverwriteActions =
    isOverwriteConflict(uploadError) && pendingUploadFile
  const uploadStage =
    uploading && uploadProgress >= 95 ? 'processing' : 'uploading'
  const normalizedTaskProgress =
    typeof uploadTaskProgress === 'number' && !Number.isNaN(uploadTaskProgress)
      ? Math.max(0, Math.min(100, Math.round(uploadTaskProgress)))
      : 0
  const hasVoiceName = Boolean(pendingVoiceName?.trim())
  const processingPercent =
    uploadStage === 'processing'
      ? Math.max(95, normalizedTaskProgress || 95)
      : Math.max(uploadProgress || 0, 1)
  const isBusy = uploading

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      onCancel={onCancel}
      className="voice-upload-details-modal"
      centered
      width={620}
      title={
        pendingUploadFile
          ? `Upload Voice: ${pendingUploadFile.name}`
          : 'Upload Voice'
      }
      footer={
        <div className="voice-upload-details-modal__footer">
          <Button key="cancel" onClick={onCancel} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            key="upload"
            type="primary"
            loading={isBusy}
            onClick={onConfirmUpload}
            disabled={!pendingUploadFile || !hasVoiceName || isBusy}
          >
            {isBusy ? 'Processing' : 'Upload'}
          </Button>
        </div>
      }
      destroyOnClose
    >
      {uploadError && !showOverwriteActions && (
        <Alert
          message={uploadError}
          type="error"
          showIcon
          className="voice-selection-modal__alert"
        />
      )}

      <div className="voice-upload-details-modal__summary">
        <div className="voice-upload-details-modal__summary-icon">
          <Icon type="audio" />
        </div>
        <div className="voice-upload-details-modal__summary-copy">
          <h4>Voice File</h4>
          <p>{pendingUploadFile?.name || 'No file selected'}</p>
        </div>
        {pendingUploadFile && (
          <span className="voice-upload-details-modal__summary-size">
            {(pendingUploadFile.size / 1024 / 1024).toFixed(2)} MB
          </span>
        )}
      </div>

      {!isBusy ? (
        <div className="voice-upload-details-modal__form">
          <div className="voice-upload-details-modal__field">
            <label>Voice Name <span aria-hidden="true">*</span></label>
            <Input
              value={pendingVoiceName}
              onChange={e => setPendingVoiceName(e.target.value)}
              placeholder="Example: Warm Narrator"
            />
          </div>
          <div className="voice-upload-details-modal__field">
            <label>Voice Description</label>
            <TextArea
              value={pendingVoiceDescription}
              onChange={e => setPendingVoiceDescription(e.target.value)}
              placeholder="Describe tone, pace, and style to improve recommendations."
              rows={4}
            />
          </div>
          <div className="voice-upload-details-modal__field">
            <label>Supported Language (Optional)</label>
            <Select
              value={pendingVoiceLanguage}
              onChange={setPendingVoiceLanguage}
              placeholder="Select supported language"
              style={{ width: '100%' }}
              optionFilterProp="children"
              allowClear
              showSearch
            >
              {LANGUAGE_OPTIONS.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      ) : (
        <div className="voice-upload-details-modal__progress voice-upload-details-modal__progress--busy">
          {uploadStage === 'uploading' ? (
            <>
              <div className="voice-upload-details-modal__status-head">
                <Icon type="cloud-upload" />
                <span>Uploading file...</span>
              </div>
              <Progress percent={processingPercent} size="small" />
            </>
          ) : (
            <>
              <div className="voice-upload-details-modal__status-processing">
                <Spin size="small" />
                <span>
                  {uploadTaskMessage ||
                    'Finalizing voice. This can take a moment...'}
                </span>
              </div>
              <Progress
                percent={processingPercent}
                size="small"
                status={
                  uploadTaskStatus === 'failed' || uploadTaskStatus === 'error'
                    ? 'exception'
                    : 'active'
                }
              />
            </>
          )}
        </div>
      )}

      {showOverwriteActions && (
        <div className="voice-upload-details-modal__conflict-card">
          <div className="voice-upload-details-modal__conflict-icon">
            <Icon type="warning" theme="filled" />
          </div>
          <div className="voice-upload-details-modal__conflict-copy">
            <h4>Voice name already exists</h4>
            <p>{uploadError}</p>
          </div>
          <div className="voice-upload-details-modal__conflict-actions">
            <Button
              onClick={onRetryUpload}
              disabled={!pendingVoiceName || !pendingVoiceName.trim()}
            >
              Try Again
            </Button>
            <Button type="danger" onClick={onOverwriteUpload}>
              Overwrite Existing Voice
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default VoiceUploadDetailsModal
